
import { useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { getPageUrl } from '@/utils/seo/urlUtils';

interface UseSEOOptions {
  title?: string;
  description?: string;
  keywords?: string[];
  image?: string;
  type?: 'website' | 'article' | 'profile';
  noIndex?: boolean;
  publishedDate?: string;
  modifiedDate?: string;
  author?: string;
  section?: string;
  tags?: string[];
}

/**
 * Hook to easily create SEO properties for pages
 */
export const useSEO = (options: UseSEOOptions = {}) => {
  const location = useLocation();
  const { language } = useLanguage();
  
  const {
    title,
    description,
    keywords = [],
    image = "/lovable-uploads/0c5638fb-e14b-45c9-962d-70e9659a760f.png",
    type = "website",
    noIndex = false,
    publishedDate,
    modifiedDate,
    author,
    section,
    tags = []
  } = options;
  
  // Default keywords that will be included in all pages
  const defaultKeywords = [
    "recut", 
    "vidéo", 
    "création", 
    "monétisation", 
    "contenu"
  ];
  
  // Combine default and custom keywords
  const allKeywords = [...new Set([...defaultKeywords, ...keywords])];
  
  // Determine current page URL
  const currentPath = location.pathname;
  const pageUrl = getPageUrl(currentPath);
  
  // Determine locale based on language context
  const locale = language === 'en' ? 'en_US' : 'fr_FR';
  
  // Build alternate language URLs
  const alternateLocales = language === 'en' 
    ? [{ locale: 'fr_FR', url: currentPath }] 
    : [{ locale: 'en_US', url: currentPath }];
  
  return {
    title,
    description,
    keywords: allKeywords.join(', '),
    ogImage: image,
    ogUrl: pageUrl,
    ogType: type,
    canonicalUrl: pageUrl,
    noIndex,
    publishedTime: publishedDate,
    modifiedTime: modifiedDate,
    authorName: author,
    locale,
    alternateLocales,
    articleSection: section,
    articleTags: tags
  };
};

export default useSEO;
